import { useTranslation } from 'react-i18next';
import { AuthCard } from '@/shared/components';
import { Button, Input } from '@/shared/components/ui';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@/shared/components/ui/form';
import { CardContent } from '@/shared/components/ui/card';
import type {
  LoginInitialRequestDto,
  SetNewPasswordRequestDto,
} from '@/features/auth-login';
import {
  setNewPassword,
  useLogin,
  usePasswordResetForm,
} from '@/features/auth-login';
import { Navigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { usePasswordChecks } from '@/shared/lib';
import { PasswordRulesList } from '@/shared/components';

export type LoginPasswordResetLocationState = {
  email: string;
  code: string;
};

const LoginPasswordResetForm = () => {
  const { t } = useTranslation();
  const location = useLocation();

  if (!location.state || !location.state.email || !location.state.code) {
    return <Navigate to="/login/password-recovery" />;
  }

  const { email, code } = location.state as LoginPasswordResetLocationState;

  const { form } = usePasswordResetForm();
  const { unmetPasswordChecks, passwordChecks } = usePasswordChecks(form);
  const { runLoginProcess, loginError, isLoginLoading } = useLogin();

  const setPasswordMutation = useMutation({
    mutationFn: setNewPassword,
    onSuccess: async (_, variables) => {
      const loginData: LoginInitialRequestDto = {
        login: email,
        password: variables.newPassword,
      };

      try {
        await runLoginProcess(loginData);
      } catch (error) {
        console.error('Auto-login failed after password reset:', error);
      }
    },
  });

  const onSubmit = async (data: { password: string }) => {
    try {
      const requestData: SetNewPasswordRequestDto = {
        email,
        code,
        newPassword: data.password,
      };
      await setPasswordMutation.mutateAsync(requestData);
    } catch (error) {
      console.error('Failed to set new password:', error);
    }
  };

  return (
    <AuthCard
      title={t('authPages.passwordResetForm.title')}
      description={t('authPages.passwordResetForm.description')}
      backButton={{
        link: '/login/password-recovery-confirmation',
        state: location.state,
      }}
      titleClassName="items-start flex-wrap">
      <CardContent className="p-0">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-[1.2rem] mb-[3.2rem]">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type="password"
                          label={t(
                            'authPages.passwordResetForm.passwordInputPlaceholder'
                          )}
                          className="p3"
                          showPasswordToggle={true}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              <PasswordRulesList
                password={form.getValues('password')}
                passwordChecks={passwordChecks}
                unmetPasswordChecks={unmetPasswordChecks}
                isFormSubmitted={form.formState.isSubmitted}
              />
            </div>

            {(setPasswordMutation.error || loginError) && (
              <p className="text-destructive p3 py-[1.2rem]">
                {(setPasswordMutation.error as Error)?.message ||
                  loginError ||
                  t('common.unknownError')}
              </p>
            )}

            <Button
              type="submit"
              disabled={setPasswordMutation.isPending || isLoginLoading}
              size="lg"
              className="w-full">
              {t('authPages.passwordResetForm.submitButton')}
            </Button>
          </form>
        </Form>
      </CardContent>
    </AuthCard>
  );
};

export { LoginPasswordResetForm };
